import React, { useRef, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { CSSTransition } from 'react-transition-group';
import styles from './Footer.module.scss';
import ForwardButton from 'components/ForwardButton';
import backButton from 'assets/images/back-button.svg';
import AudioCaptions from 'components/AudioCaptions';
import { handleRoundEnd } from 'store/actions/activity';
import { getRedirectPathname } from 'utils/getUrlParams';
import {
  A11ySettingsPopover,
  ExternalControl
} from '@lwtears/lwt-common-frontend/lib/@a11y/A11ySettings';
import { useLwtStore } from '@lwtears/lwt-common-frontend/lib/LwtApp';
import { applyA11ySettings } from '@lwtears/lwt-common-frontend/lib/@common/util/a11y-util';
import { getAudioVendor } from 'utils/accessibilityUtils';
import { t } from '../../translations';

const backRedirect = (pathname, appMode, module) => {
  return getRedirectPathname(appMode, pathname, module);
};

const Footer = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const {
    appMode,
    module,
    forwardButton,
    loading,
    isLiteVersion,
    hasAccessibilityMode
  } = useSelector(state => state.ui);
  const { showBackgroundOfAward } = useSelector(state => state.award);
  const { activities, currentRoundIndex, flowComplete } = useSelector(state => state.activity);
  const { showAward } = useSelector(state => state.award);
  const dispatch = useDispatch();
  const paths = ['/pin-recovery', '/app-selection', `/${module}/splash`, '/tester'];
  const [state] = useLwtStore();
  const [ariaLabel, setAriaLabel] = useState('');

  const currentIndexRef = useRef(currentRoundIndex);
  const flowCompleteRef = useRef(flowComplete);

  useEffect(() => {
    currentIndexRef.current = currentRoundIndex;
  }, [currentRoundIndex]);

  useEffect(() => {
    flowCompleteRef.current = flowComplete;
  }, [flowComplete]);

  useEffect(() => {
    if (pathname.includes('reward-screen')) {
      setAriaLabel(t('GoBackAppSelection'));
    }
    if (pathname.includes('activity')) {
      setAriaLabel(t('GoBackARewardScreen'));
    }
  }, [pathname]);

  const {
    settings: {
      accessibility: {
        closedCaptionsSettings: { showClosedCaptions, displayCCControl },
        displaySettings: { contentScaling }
      }
    }
  } = state;
  const canGoBack = !showAward;

  const redirectToNextActivity = () => {
    const { type } = activities[currentIndexRef.current];
    // redirect if activity complete
    if (flowCompleteRef.current) {
      navigate(appMode === 'tester' ? '/tester' : `/${module}/reward-screen`);
    } else {
      navigate(`/${module}/activity/${type}`);
    }
  };

  const endRound = async () => {
    await dispatch(handleRoundEnd());
    process.nextTick(() => redirectToNextActivity());
  };

  if (paths.includes(pathname)) return null;
  if (isLiteVersion && (!showClosedCaptions || !displayCCControl) && !hasAccessibilityMode)
    return null;

  return (
    <CSSTransition
      in={!loading && !showBackgroundOfAward}
      timeout={500}
      mountOnEnter
      unmountOnExit
      classNames={{
        enter: 'fade-enter',
        enterActive: 'fade-enter-active',
        exit: 'fade-exit'
      }}
    >
      <div
        className={[styles.footerContainer, styles[`footerContainer${contentScaling}`]].join(' ')}
      >
        {!isLiteVersion && (
          <div className={styles.backButtonDiv} id="backButtonDiv">
            <Link
              to={backRedirect(pathname, appMode, module)}
              className={[styles.backButton, styles[`backButton${contentScaling}`]].join(' ')}
              role="button"
              aria-label={ariaLabel}
              style={{ pointerEvents: canGoBack ? 'auto' : 'none' }}
              tabIndex="0"
            >
              <img src={backButton} alt="" />
            </Link>
          </div>
        )}
        <div id="ccDiv" className={styles.ccDiv}>
          {showClosedCaptions && displayCCControl && <AudioCaptions />}
        </div>
        {hasAccessibilityMode && (
          <div style={{ marginRight: 80 }}>
            <A11ySettingsPopover
              theme="light"
              onSubmit={response => applyA11ySettings(response.data, getAudioVendor())}
              externalControls={[ExternalControl.AUDIO_CONTROLS, ExternalControl.CC_BUTTON]}
            />
          </div>
        )}
        <div className={styles.forwardButtonDiv}>
          {forwardButton.show && !isLiteVersion && (
            <ForwardButton onClick={endRound} contentScaling={contentScaling} />
          )}
        </div>
      </div>
    </CSSTransition>
  );
};

export default Footer;
